import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { Gift, Copy, CheckCheck, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function GiftSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sid) return;
    let stop = false;
    const poll = async (n = 0) => {
      if (stop || n > 10) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setStatus(data);
        setAttempts(n + 1);
        if (data.gift?.code) return;          // done
        if (data.payment_status === "paid") return; // paid but code being generated — stop polling after a moment
      } catch { /* transient poll error; will retry on next tick */ }
      setTimeout(() => poll(n + 1), 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sid]);

  const code = status?.gift?.code;
  const recipient = status?.gift?.recipient_email;

  const copyCode = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy"); }
  };

  const redeemUrl = code ? `${window.location.origin}/redeem?code=${code}` : "";

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 sm:px-8 py-14" data-testid="gift-success-page">
        <div className="overline mb-3"><span className="red-bar" />Gift purchased</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-3">
          Gift sent.
        </h1>
        <p className="text-zinc-700 mb-8 max-w-xl leading-relaxed">
          Your payment is confirmed. {recipient ? <>We're emailing <strong>{recipient}</strong> their redemption code now.</> : "We're processing the gift and emailing the recipient."}
        </p>

        {!code ? (
          <div className="card-tech p-8 text-center" data-testid="gift-success-processing">
            <Gift className="w-10 h-10 text-red-600 mx-auto mb-3" />
            <div className="font-heading text-lg font-bold mb-1">Generating your gift code…</div>
            <div className="text-xs text-zinc-500 font-mono">Attempt {attempts} of 10 · usually under 10 seconds</div>
          </div>
        ) : (
          <div className="card-tech p-8 border-green-300 bg-green-50/40" data-testid="gift-success-code-card">
            <div className="flex items-center gap-2 mb-4">
              <CheckCheck className="w-5 h-5 text-green-600" />
              <div className="overline"><span className="red-bar" />Gift code</div>
            </div>
            <div className="font-mono text-xl sm:text-2xl font-bold tracking-wider bg-zinc-950 text-white px-4 py-3 mb-4 text-center" data-testid="gift-success-code">
              {code}
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyCode} className="btn-outline" data-testid="gift-success-copy-btn">
                {copied ? <CheckCheck className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : "Copy code"}
              </button>
              {redeemUrl && (
                <a href={redeemUrl} target="_blank" rel="noreferrer" className="btn-outline">
                  <ArrowRight className="w-4 h-4" /> Redemption link
                </a>
              )}
            </div>
            <div className="mt-6 pt-5 border-t border-green-200 text-sm text-zinc-700 flex items-start gap-2">
              <Mail className="w-4 h-4 text-red-600 mt-0.5" />
              <div>
                Emailed to <strong>{recipient}</strong>. You'll get another email the moment the code is redeemed.
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex gap-3 flex-wrap">
          <Link to="/dashboard"><button className="btn-outline">Back to dashboard</button></Link>
          <Link to="/enroll?tab=gift"><button className="btn-outline">Send another gift</button></Link>
        </div>
      </main>
    </div>
  );
}
